// IssueForm - §10 지연 입력 3항목 (지연사유 / 조치내용 / 예상 완료일).
//
// **지연으로 자동 판정된 항목에서만 열린다** (REQ-DELAY-008). 담당자는
// "지연입니까?" 라는 질문을 받지 않는다 - 시스템이 이미 판정했고, 물어보는
// 것은 사유뿐이다. 정상 진행 건에서는 이 폼의 진입점 자체가 없다.
//
// 진입점이 두 곳(상세화면 지연 행 / 지연·이슈 화면)이지만 같은 엔드포인트를
// 부르므로 중복 입력이 아니다 (추적표 §22).
import { Button, DateField, Dialog, Field, Textarea } from "@/components/ui";
import { type IssueTargetType, createIssue, deleteIssue, updateIssue } from "@/lib/api";
import { useState } from "react";

export interface IssueTarget {
  devItemId: string;
  targetType: IssueTargetType;
  targetId: string;
  targetLabel: string;
  /** null 이면 POST, 있으면 PATCH (§05 A-7) */
  issueId: string | null;
  reason: string;
  action: string;
  expectedDate: string;
}

export const IssueForm = ({
  target,
  onClose,
  onSaved,
  onError,
}: {
  /** null 이면 닫혀 있다 */
  target: IssueTarget | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}) => {
  const [reason, setReason] = useState("");
  const [action, setAction] = useState("");
  const [expectedDate, setExpectedDate] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState<string | null>(null);

  const wanted = target ? `${target.targetType}:${target.targetId}` : null;
  if (seed !== wanted) {
    setSeed(wanted);
    setReason(target?.reason ?? "");
    setAction(target?.action ?? "");
    setExpectedDate(target?.expectedDate ?? "");
    setError("");
  }

  const submit = async () => {
    if (!target) return;
    if (!reason.trim()) {
      setError("지연사유를 입력해 주세요.");
      return;
    }
    setBusy(true);
    try {
      if (target.issueId) {
        await updateIssue(target.issueId, { reason: reason.trim(), action, expectedDate });
      } else {
        await createIssue({
          devItemId: target.devItemId,
          targetType: target.targetType,
          targetId: target.targetId,
          reason: reason.trim(),
          action,
          expectedDate,
        });
      }
      onSaved("저장했어요.");
      onClose();
    } catch (err) {
      onError(
        err instanceof Error ? err.message : "저장하지 못했어요. 잠시 후 다시 시도해 주세요."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={target ? `지연사유 - ${target.targetLabel}` : "지연사유"}
      width={520}
      footer={
        <>
          {/* 잘못 입력한 이슈를 지운다 (§05 §9-4). 대상이 여전히 지연이면
              목록에 `사유 미입력` 행으로 다시 나타난다 - 지연 자체가
              사라지는 게 아니라 사유만 지워진다. */}
          {target?.issueId && (
            <Button
              variant="ghost"
              size="md"
              disabled={busy}
              onClick={async () => {
                if (!target.issueId) return;
                setBusy(true);
                try {
                  await deleteIssue(target.issueId);
                  onSaved("삭제했어요.");
                  onClose();
                } catch (err) {
                  onError(err instanceof Error ? err.message : "삭제하지 못했어요.");
                } finally {
                  setBusy(false);
                }
              }}
            >
              사유 삭제
            </Button>
          )}
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button variant="primary" size="md" onClick={submit} loading={busy}>
            {target?.issueId ? "저장" : "지연사유 등록하기"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-s4">
        <p className="text-label" style={{ color: "var(--text-tertiary)" }}>
          일정보다 늦어지고 있어요. 지연사유를 남기면 관리자가 더 빨리 파악할 수 있어요.
        </p>
        <Field label="지연사유" required error={error}>
          {(p) => (
            <Textarea
              {...p}
              value={reason}
              onChange={(v) => {
                setReason(v);
                if (error) setError("");
              }}
              rows={3}
              maxLength={500}
              placeholder="예: 소재 입고 지연, 설비 점검 등 지연 원인을 간단히 적어주세요."
            />
          )}
        </Field>
        <Field label="조치내용">
          {(p) => <Textarea {...p} value={action} onChange={setAction} rows={2} maxLength={500} />}
        </Field>
        <Field label="예상 완료일">
          {(p) => <DateField {...p} value={expectedDate} onChange={setExpectedDate} />}
        </Field>
      </div>
    </Dialog>
  );
};
